import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Evaluator } from 'services/process/test360/test360UpdateDraft'
import { Test360Props } from 'types/common'
import {
  Test360ApiResponse,
  Test360Competence,
  Test360EvaluatedUser,
} from 'views/screens/Evaluated-User/pages/QuestionsTest360/types'

export type InitialState = typeof initialState

export interface Test360Answer {
  competenceId: number
  questionIndex: number
  evaluatedUserId: string
  score: number
}

export interface Test360Answers {
  [competenceId: number]: {
    [questionIndex: number]: {
      [evaluatedUserId: string]: number
    }
  }
}

interface IState {
  test360Data: Test360Props[] | null
  loading: boolean
  error: boolean
  showSummary: boolean
  showIntro: boolean
  evaluatedUsers: Evaluator[] | null
  evaluators: Evaluator[] | null
  has360Test: boolean
  finalDate: string
  answers: Test360Answers
  // New fields for API data
  competences: Test360Competence[]
  test360EvaluatedUsers: Test360EvaluatedUser[]
}

export const initialState: IState = {
  test360Data: [],
  loading: false,
  error: false,
  showSummary: false,
  showIntro: true,
  evaluatedUsers: [],
  evaluators: [],
  has360Test: false,
  finalDate: '',
  answers: {},
  // New fields
  competences: [],
  test360EvaluatedUsers: [],
}

const test360Slice = createSlice({
  name: 'test360',
  initialState,
  reducers: {
    set360Data(state, action: PayloadAction<Test360Props[]>) {
      state.test360Data = action.payload
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setError(state, action: PayloadAction<boolean>) {
      state.error = action.payload
    },
    setShowSummary(state, action: PayloadAction<boolean>) {
      state.showSummary = action.payload
    },
    setShowIntro(state, action: PayloadAction<boolean>) {
      state.showIntro = action.payload
    },
    clear360Data(state) {
      state.test360Data = []
      state.loading = false
      state.error = false
      state.showSummary = false
      state.showIntro = true
      state.evaluatedUsers = []
      state.evaluators = []
      state.has360Test = false
      state.answers = {}
    },
    setEvaluatedUsers(state, action: PayloadAction<Evaluator[]>) {
      state.evaluatedUsers = action.payload
    },
    setEvaluators(state, action: PayloadAction<Evaluator[]>) {
      state.evaluators = action.payload
    },
    clearEvaluatedUsers(state) {
      state.evaluatedUsers = []
    },
    clearEvaluators(state) {
      state.evaluators = []
    },
    setHas360Test(state, action: PayloadAction<boolean>) {
      state.has360Test = action.payload
    },
    setFinalDate(state, action: PayloadAction<string>) {
      state.finalDate = action.payload
    },
    saveAnswer(state, action: PayloadAction<Test360Answer>) {
      try {
        const { competenceId, questionIndex, evaluatedUserId, score } = action.payload

        if (!competenceId || typeof competenceId !== 'number') {
          console.error('Invalid competenceId:', competenceId)
          return
        }

        if (questionIndex === undefined || questionIndex === null || typeof questionIndex !== 'number') {
          console.error('Invalid questionIndex:', questionIndex)
          return
        }

        if (!evaluatedUserId || typeof evaluatedUserId !== 'string') {
          console.error('Invalid evaluatedUserId:', evaluatedUserId)
          return
        }

        if (score === undefined || score === null || typeof score !== 'number') {
          console.error('Invalid score:', score)
          return
        }

        if (!state.answers || typeof state.answers !== 'object') {
          state.answers = {}
        }

        if (!state.answers[competenceId] || typeof state.answers[competenceId] !== 'object') {
          state.answers[competenceId] = {}
        }

        if (
          !state.answers[competenceId][questionIndex] ||
          typeof state.answers[competenceId][questionIndex] !== 'object'
        ) {
          state.answers[competenceId][questionIndex] = {}
        }

        state.answers[competenceId][questionIndex][evaluatedUserId] = score
      } catch (error) {
        console.error('Error in saveAnswer reducer:', error)
      }
    },
    clearAnswer(
      state,
      action: PayloadAction<{ competenceId: number; questionIndex: number; evaluatedUserId: string }>
    ) {
      try {
        const { competenceId, questionIndex, evaluatedUserId } = action.payload

        if (!competenceId || typeof competenceId !== 'number') {
          console.error('Invalid competenceId in clearAnswer:', competenceId)
          return
        }

        if (questionIndex === undefined || questionIndex === null || typeof questionIndex !== 'number') {
          console.error('Invalid questionIndex in clearAnswer:', questionIndex)
          return
        }

        if (!evaluatedUserId || typeof evaluatedUserId !== 'string') {
          console.error('Invalid evaluatedUserId in clearAnswer:', evaluatedUserId)
          return
        }

        if (
          state.answers &&
          state.answers[competenceId] &&
          state.answers[competenceId][questionIndex] &&
          state.answers[competenceId][questionIndex][evaluatedUserId] !== undefined
        ) {
          delete state.answers[competenceId][questionIndex][evaluatedUserId]
        }
      } catch (error) {
        console.error('Error in clearAnswer reducer:', error)
      }
    },
    clearAllAnswers(state) {
      state.answers = {}
    },
    setTest360Data(state, action: PayloadAction<Test360ApiResponse>) {
      state.competences = action.payload.competences
      state.test360EvaluatedUsers = action.payload.evaluatedUsers
    },
    setCompetences(state, action: PayloadAction<Test360Competence[]>) {
      state.competences = action.payload
    },
    setTest360EvaluatedUsers(state, action: PayloadAction<Test360EvaluatedUser[]>) {
      state.test360EvaluatedUsers = action.payload
    },
    updateCompetenceProgress(
      state,
      action: PayloadAction<{ competenceId: number; answeredQuestions: number; isComplete: boolean }>
    ) {
      const { competenceId, answeredQuestions, isComplete } = action.payload
      const competenceIndex = state.competences.findIndex((comp) => comp.competenceId === competenceId)

      if (competenceIndex !== -1) {
        state.competences[competenceIndex].answeredQuestions = answeredQuestions
        state.competences[competenceIndex].isComplete = isComplete

        // Enable next competence if current is complete
        if (isComplete && competenceIndex < state.competences.length - 1) {
          state.competences[competenceIndex + 1].isEnabled = true
        }
      }
    },
    clearTest360Data(state) {
      state.competences = []
      state.test360EvaluatedUsers = []
      state.answers = {}
    },
    loadExistingAnswers(state, action: PayloadAction<Test360Answers>) {
      state.answers = { ...state.answers, ...action.payload }
    },
  },
})

export const {
  set360Data,
  setLoading,
  setError,
  setShowSummary,
  setShowIntro,
  clear360Data,
  setEvaluatedUsers,
  setEvaluators,
  clearEvaluatedUsers,
  clearEvaluators,
  setHas360Test,
  setFinalDate,
  saveAnswer,
  clearAnswer,
  clearAllAnswers,
  setTest360Data,
  setCompetences,
  setTest360EvaluatedUsers,
  updateCompetenceProgress,
  clearTest360Data,
  loadExistingAnswers,
} = test360Slice.actions

export const test360Reducer = test360Slice.reducer
